import { createSelector } from 'reselect';
import { RootState } from '../../store/store';

const selectAuditState = (state: RootState) => state.audit;

export const auditSelectors = {
    // Basic selectors
    auditLogs: createSelector(selectAuditState, (audit) => audit.auditLogs),
    currentFilters: createSelector(selectAuditState, (audit) => audit.currentFilters),
    pagination: createSelector(selectAuditState, (audit) => audit.pagination),
    
    // Search selectors
    searchResults: createSelector(selectAuditState, (audit) => audit.searchResults),
    searchQuery: createSelector(selectAuditState, (audit) => audit.searchQuery),
    
    // Entity audit selectors
    entityAudit: createSelector(selectAuditState, (audit) => audit.entityAudit),
    
    // Statistics selectors
    statistics: createSelector(selectAuditState, (audit) => audit.statistics),
    
    // Metadata selectors
    entityTypes: createSelector(selectAuditState, (audit) => audit.entityTypes),
    auditUsers: createSelector(selectAuditState, (audit) => audit.auditUsers),
    
    // UI state selectors
    loading: createSelector(selectAuditState, (audit) => audit.loading),
    error: createSelector(selectAuditState, (audit) => audit.error),
    
    // Computed selectors
    hasMoreLogs: createSelector(selectAuditState, (audit) => audit.pagination.hasMore),
    
    totalLogCount: createSelector(selectAuditState, (audit) => audit.pagination.totalCount),
    
    // Get logs for display (main logs or search results)
    displayLogs: createSelector(
        selectAuditState,
        (audit) => audit.searchQuery ? audit.searchResults : audit.auditLogs
    ),
    
    // Check if in search mode
    isSearchMode: createSelector(
        selectAuditState,
        (audit) => audit.searchQuery.length > 0
    ),
    
    // Get entity types for dropdown
    entityTypeOptions: createSelector(
        selectAuditState,
        (audit) => audit.entityTypes.map(type => ({ value: type, label: type }))
    ),
    
    // Get users for dropdown  
    userOptions: createSelector(
        selectAuditState,
        (audit) => audit.auditUsers.map(user => ({ value: user.userId, label: user.userName }))
    ),
    
    // Action options for dropdown
    actionOptions: createSelector(
        () => [
            { value: 'CREATE', label: 'Created' },
            { value: 'UPDATE', label: 'Updated' },
            { value: 'DELETE', label: 'Deleted' }
        ]
    ),
    
    // Statistics charts data
    actionChartData: createSelector(
        selectAuditState,
        (audit) => {
            if (!audit.statistics) return null;
            const { changesByAction } = audit.statistics;
            return [
                { name: 'Created', value: changesByAction.CREATE, color: '#22c55e' },
                { name: 'Updated', value: changesByAction.UPDATE, color: '#f59e0b' },
                { name: 'Deleted', value: changesByAction.DELETE, color: '#ef4444' }
            ];
        }
    ),
    
    entityTypeChartData: createSelector(
        selectAuditState,
        (audit) => {
            if (!audit.statistics) return null;
            const { changesByEntityType } = audit.statistics;
            return Object.entries(changesByEntityType).map(([entityType, count]) => ({
                name: entityType,
                value: count
            }));
        }
    ),
    
    topUsersData: createSelector(
        selectAuditState,
        (audit) => {
            if (!audit.statistics) return [];
            return audit.statistics.changesByUser.slice(0, 10); // Top 10 users
        }
    ),
    
    mostActiveEntitiesData: createSelector(
        selectAuditState,
        (audit) => {
            if (!audit.statistics) return [];
            return audit.statistics.mostActiveEntities.slice(0, 10); // Top 10 entities
        }
    ),

    // Count active filters
    activeFiltersCount: createSelector(
        selectAuditState,
        (audit) => {
            const filters = audit.currentFilters;
            let count = 0;

            if (filters.entityTypes && filters.entityTypes.length > 0) count++;
            if (filters.userIds && filters.userIds.length > 0) count++;
            if (filters.actions && filters.actions.length > 0) count++;
            if (filters.searchTerm) count++;
            if (filters.range) count++;
            if (filters.startDate || filters.endDate) count++;

            return count;
        }
    ),

    // Parse filter fields for display (like other pages)
    filterFields: createSelector(
        selectAuditState,
        (audit) => {
            const filters = audit.currentFilters;
            const fields: Array<{ field: string; value: any }> = [];

            if (filters.entityTypes && filters.entityTypes.length > 0) {
                fields.push({ field: 'entityTypes', value: filters.entityTypes });
            }
            if (filters.userIds && filters.userIds.length > 0) {
                fields.push({ field: 'userIds', value: filters.userIds });
            }
            if (filters.actions && filters.actions.length > 0) {
                fields.push({ field: 'actions', value: filters.actions });
            }
            if (filters.searchTerm) {
                fields.push({ field: 'searchTerm', value: filters.searchTerm });
            }
            if (filters.range) {
                fields.push({ field: 'range', value: filters.range });
            }
            if (filters.startDate) {
                fields.push({ field: 'startDate', value: filters.startDate });
            }
            if (filters.endDate) {
                fields.push({ field: 'endDate', value: filters.endDate });
            }

            return fields;
        }
    ),

    // Grouped audit logs selectors
    groupedAuditLogs: createSelector(selectAuditState, (audit) => audit.groupedAuditLogs),
    groupedPagination: createSelector(selectAuditState, (audit) => audit.groupedPagination),

    // Check if there are more grouped logs to load
    hasMoreGroupedLogs: createSelector(
        selectAuditState,
        (audit) => audit.groupedPagination.hasMore
    ),

    // Get total groups count
    totalGroupsCount: createSelector(
        selectAuditState,
        (audit) => audit.groupedPagination.totalGroups
    )
};